'use client'

import React, { useMemo, useRef, useEffect } from 'react'
import { ISeriesApi, IChartApi, LineStyle, LineWidth, LineSeries, UTCTimestamp } from 'lightweight-charts'
import { 
  Channel, 
  Point,
  calculateChannelLines
} from '../../types/drawing'

interface ChannelProps {
  drawing: Channel
  chart: IChartApi
  isActive: boolean
  onUpdate?: (drawing: Channel) => void
  onComplete?: (drawing: Channel) => void
}

/**
 * Channel Drawing Component
 * 
 * Supports both parallel channels and regression channels:
 * - Parallel channels: Fixed width channels based on trend
 * - Standard channels: Three-point channels with upper and lower bounds
 * - Supports extending lines left/right
 * - Optional midline for better visualization
 */
const ChannelComponent: React.FC<ChannelProps> = ({
  drawing,
  chart,
  isActive,
  onUpdate,
  onComplete
}) => {
  const seriesRefs = useRef<Map<string, ISeriesApi<'Line'>>>(new Map())

  // Calculate channel lines based on drawing points
  const channelLines = useMemo(() => {
    if (drawing.points.length < 2) return null
    
    const [startPoint, endPoint, thirdPoint] = drawing.points
    
    if (drawing.type === 'parallel-channel' && drawing.channelWidth) {
      return calculateChannelLines(startPoint, endPoint, drawing.channelWidth)
    } else if (drawing.points.length >= 3) {
      // Standard channel with three points
      const upperLine: [Point, Point] = [startPoint, endPoint]
      const lowerLine: [Point, Point] = [
        { time: startPoint.time, price: thirdPoint.price },
        { time: endPoint.time, price: thirdPoint.price + (endPoint.price - startPoint.price) }
      ]
      const midLine: [Point, Point] = [
        { time: startPoint.time, price: (startPoint.price + thirdPoint.price) / 2 },
        { time: endPoint.time, price: (endPoint.price + (thirdPoint.price + (endPoint.price - startPoint.price))) / 2 }
      ]
      
      return { upperLine, lowerLine, midLine }
    }
    
    return null
  }, [drawing.points, drawing.type, drawing.channelWidth])

  // Create or update line series for channel
  useEffect(() => {
    if (!chart || !channelLines) return

    // Clear existing series
    seriesRefs.current.forEach(series => {
      try {
        if (chart && series) {
          chart.removeSeries(series)
        }
      } catch (error) {
        // Safely ignore cleanup errors when chart is destroyed
        console.warn('Failed to remove series during cleanup:', error)
      }
    })
    seriesRefs.current.clear()

    // Create upper line
    const upperSeries = chart.addSeries(LineSeries, {
      color: drawing.style.lineColor,
      lineWidth: drawing.style.lineWidth as LineWidth,
      lineStyle: drawing.style.lineStyle === 'dashed' ? LineStyle.Dashed : 
                 drawing.style.lineStyle === 'dotted' ? LineStyle.Dotted : 
                 LineStyle.Solid,
      title: 'Channel Upper',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false
    })

    // Create lower line
    const lowerSeries = chart.addSeries(LineSeries, {
      color: drawing.style.lineColor,
      lineWidth: drawing.style.lineWidth as LineWidth,
      lineStyle: drawing.style.lineStyle === 'dashed' ? LineStyle.Dashed : 
                 drawing.style.lineStyle === 'dotted' ? LineStyle.Dotted : 
                 LineStyle.Solid,
      title: 'Channel Lower',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false
    })

    // Upper line data
    const upperData = [
      {
        time: (drawing.extend.left ? 0 : channelLines.upperLine[0].time) as UTCTimestamp,
        value: channelLines.upperLine[0].price
      },
      {
        time: (drawing.extend.right ? 999999999999 : channelLines.upperLine[1].time) as UTCTimestamp,
        value: channelLines.upperLine[1].price
      }
    ]

    // Lower line data
    const lowerData = [
      {
        time: (drawing.extend.left ? 0 : channelLines.lowerLine[0].time) as UTCTimestamp,
        value: channelLines.lowerLine[0].price
      },
      {
        time: (drawing.extend.right ? 999999999999 : channelLines.lowerLine[1].time) as UTCTimestamp,
        value: channelLines.lowerLine[1].price
      }
    ]

    upperSeries.setData(upperData)
    lowerSeries.setData(lowerData)

    seriesRefs.current.set('upper', upperSeries)
    seriesRefs.current.set('lower', lowerSeries)

    // Add midline if enabled
    if (drawing.showMidline && channelLines.midLine) {
      const midSeries = chart.addSeries(LineSeries, {
        color: drawing.style.lineColor,
        lineWidth: Math.max(1, drawing.style.lineWidth - 1) as LineWidth,
        lineStyle: LineStyle.Dashed,
        title: 'Channel Mid',
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false
      })

      const midData = [
        {
          time: (drawing.extend.left ? 0 : channelLines.midLine[0].time) as UTCTimestamp,
          value: channelLines.midLine[0].price
        },
        {
          time: (drawing.extend.right ? 999999999999 : channelLines.midLine[1].time) as UTCTimestamp,
          value: channelLines.midLine[1].price
        }
      ]

      midSeries.setData(midData)
      seriesRefs.current.set('mid', midSeries)
    }

    return () => {
      seriesRefs.current.forEach(series => {
        try {
          if (chart && series) {
            chart.removeSeries(series)
          }
        } catch (error) {
          // Safely ignore cleanup errors when chart is destroyed
          console.warn('Failed to remove series during cleanup:', error)
        }
      })
      seriesRefs.current.clear()
    }
  }, [chart, drawing, channelLines])

  // Handle mouse interactions for editing
  const handleMouseEvents = () => {
    if (!isActive || !chart) return

    // TODO: Implement mouse event handlers for editing
    // - Click and drag to move channel lines
    // - Right-click for context menu
    // - Handle point manipulation for channel width adjustment
  }

  return (
    <>
      {isActive && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'auto',
            cursor: 'crosshair'
          }}
          onMouseDown={handleMouseEvents}
        />
      )}
    </>
  )
}

export default ChannelComponent

// Channel Factory Functions
export const createParallelChannel = (
  id: string,
  startPoint: Point,
  endPoint: Point,
  width: number,
  options?: Partial<Channel>
): Channel => {
  return {
    id,
    type: 'parallel-channel',
    points: [startPoint, endPoint],
    channelWidth: width,
    showMidline: true,
    extend: {
      left: false,
      right: true
    },
    style: {
      lineColor: '#2962FF',
      lineWidth: 1,
      lineStyle: 'solid',
      textColor: '#E0E3EB',
      fontSize: 12
    },
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 1,
    ...options
  }
}

export const createStandardChannel = (
  id: string,
  startPoint: Point,
  endPoint: Point,
  thirdPoint: Point,
  options?: Partial<Channel>
): Channel => {
  return {
    id,
    type: 'channel',
    points: [startPoint, endPoint, thirdPoint],
    showMidline: true,
    extend: {
      left: false,
      right: true
    },
    style: {
      lineColor: '#2962FF',
      lineWidth: 1,
      lineStyle: 'solid',
      textColor: '#E0E3EB',
      fontSize: 12
    },
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 1,
    ...options
  }
}

// Utility functions for channel calculations
export const calculateChannelWidth = (
  upperPoint: Point,
  lowerPoint: Point
): number => {
  return Math.abs(upperPoint.price - lowerPoint.price)
}

export const isPointWithinChannel = (
  point: Point,
  channel: Channel,
  tolerance: number = 0.01
): boolean => {
  // TODO: Implement point-in-channel detection
  // This would check if a price point falls within the channel boundaries
  return false
}

// Popular channel configurations
export const CHANNEL_PRESETS = {
  tight: { width: 0.5, color: '#FF6B6B' },
  normal: { width: 1.0, color: '#4ECDC4' },
  wide: { width: 2.0, color: '#45B7D1' },
  custom: { width: 1.5, color: '#96CEB4' }
} 